import { MoreHorizontal, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";

interface SyncToggleCardProps {
  /** Current value of `GoogleBusinessConnection.syncEnabled`. */
  syncEnabled: boolean;
  /** Fired on toggle. Hook is responsible for the actual mutation + toast. */
  onChange: (enabled: boolean) => void;
  /** True while the mutation is in flight — disables the Switch and shows a spinner state. */
  isPending: boolean;
  /** True if the current user is an admin (renders three-dot menu). */
  isAdmin: boolean;
  /** Triggered when admin clicks "Sync now". */
  onSyncNow: () => void;
  /** True while the sync is in progress. */
  isSyncing: boolean;
}

/**
 * Card-shaped toggle for the hourly Google review sync cron. The worker
 * already filters `where: syncEnabled = true`, so flipping this flag is
 * sufficient to pause/resume hourly review pulls — no extra unschedule
 * step needed.
 *
 * Lives below the connection status strip on the Google Business settings
 * page; only rendered when a connection exists (toggling a non-existent
 * connection makes no sense).
 */
export function SyncToggleCard({
  syncEnabled,
  onChange,
  isPending,
  isAdmin,
  onSyncNow,
  isSyncing,
}: SyncToggleCardProps) {
  const { t } = useTranslation("settings");

  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="rounded-md bg-indigo-50 dark:bg-indigo-950 p-2 shrink-0">
          <RefreshCw className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0">
          <label htmlFor="google-sync-enabled" className="font-medium text-sm cursor-pointer block">
            {t("reviews.syncReviewsFromGoogle.title")}
          </label>
          <p className="text-xs text-muted-foreground mt-0.5">
            {syncEnabled
              ? t("reviews.syncReviewsFromGoogle.descriptionOn")
              : t("reviews.syncReviewsFromGoogle.descriptionOff")}
          </p>
        </div>
        <Switch
          id="google-sync-enabled"
          checked={syncEnabled}
          onCheckedChange={onChange}
          disabled={isPending}
          aria-label={t("reviews.syncReviewsFromGoogle.title")}
        />
        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled={isSyncing}>
                <MoreHorizontal className="w-4 h-4" />
                <span className="sr-only">{t("reviews.syncNow")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onSyncNow} disabled={isSyncing}>
                {t("reviews.syncNow")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </CardContent>
    </Card>
  );
}
